import React, { useState, useEffect, useContext, useRef } from "react";
import Skeleton from "react-loading-skeleton";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { jsonToCSV } from "react-papaparse";
import moment from "moment";
import _ from "lodash";
import { ToastContainer, toast } from "react-toastify";
import { SettingsContext } from "contexts/SettingsContext";
import Pagination from "utils/Pagination";
import { Title } from "utils/Title";
import { errorHandler } from "utils/errorHandler";
// import log from "utils/log";
import {skuLabelTemplateQR} from "utils/skuLabelTemplateQR";
import ItemsHeader from "./ItemsHeader";
import {
  deleteItem,
  fetchItems,
  fetchItemsSearch,
  getItemDoc,
  submitItem,
} from "utils/items";
import ModalItem from "components/modals/ModalProduction";
import { generatePMTHandler } from "utils/pretreatment";
import ProductionsList from "./ProductionsList";
import PageMainHeader from "components/PageMainHeader";
import { AuthContext } from "contexts/AuthContext";
import PageMenuHeader from "components/PageMenuHeader";
import Search from "components/Search";
import Banner from "@leafygreen-ui/banner";
import { ModalPreloader } from "utils/Preloader";
import { getGraphics } from "utils/graphics";
import ScrollTop from "utils/ScrollTop";
import { barcodeScannedHandler } from "utils/barcodeScannedHandler";
import { getInventoryAreas } from "utils/inventoryArea";
import socketIOClient from "socket.io-client";

const title = "Productions";
const socket = socketIOClient(process.env.REACT_APP_SOCKET_ENDPOINT);

const initialItem = {
  sku: "",
  description: "",
  category: "",
  component: "",
  size: "",
  color: "",
  graphicPosition: "",
  ripEnv: "",
  polarisRecipe: "",
  imageUrl: "",
  createdAt: "",
  modifiedAt: "",
  pretreatmentBarcode: "",
  active: true,
  type: "production",
};

const limit = 50;
const selectedItemsType = "production";

const useQueryOptions = {
  refetchOnWindowFocus: false,
  staleTime: 1000 * 60 * 1,
};

export default function Productions() {
  const [loading, setLoading] = useState(false);
  const [item, setItem] = useState(initialItem);
  const [mode, setMode] = useState("create");
  const [msg, setMsg] = useState(null);
  const [page, setPage] = useState(1);
  const [searchTxt, setSearchTxt] = useState(null);
  const [selectedExportMode, setSelectedExportMode] = useState("retrieved");
  const { settings } = useContext(SettingsContext);
  const [itemModalIsOpen, setItemModalIsOpen] = useState(false);
  const { user } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const componentValueRef = useRef("");

  // console.log("[Productions] settings: ", settings);

  const {
    data: items,
    isLoading,
    isError,
    error,
    isFetching,
    refetch,
  } = useQuery(
    ["productions", page],
    async () =>
      await fetchItems({
        query: { type: selectedItemsType },
        options: { page, limit },
      }),
    {
      ...useQueryOptions,
      enabled: false,
    }
  );

  useEffect(() => {
    // console.log("items in hook: ", items);
    if (items?.totalPages > page) {
      queryClient.prefetchQuery(
        ["productions", page + 1],
        () =>
          fetchItems({
            query: { type: selectedItemsType },
            options: { page: page + 1, limit },
          }),
        {
          ...useQueryOptions,
        }
      );
    }
    // eslint-disable-next-line
  }, [page, items, queryClient]);

  useEffect(() => {
    console.log("* page hooks init");
    if (_.isNil(searchTxt)) {
      refetch();
    } else {
      fetchSearchResults.mutate(searchTxt);
    }
    // eslint-disable-next-line
  }, [page, searchTxt]);

  const onBarcodeScanned = (data) => {
    console.log("* onBarcodeScanned init");
    console.log("[onBarcodeScanned] data: ", data);
    const { sku } = barcodeScannedHandler(data);
    setPage(1);
    setSearchTxt(sku);
    fetchSearchResults.mutate(sku);
  }

  const socketOnBarcodeScanned = async () => {
    socket.on("on-barcode-scanned", async (data) => {
      console.log("* on-barcode-scanned")
      console.log("[on-barcode-scanned] data: ", data);
      const { sku } = barcodeScannedHandler(data);
      setPage(1);
      setSearchTxt(sku);
      fetchSearchResults.mutate(sku);
    })
  }

  //onBarcodeScanned hook
  useEffect(() => {
    const listner = window?.printflo_api && window.printflo_api.receive("serialport_readline", onBarcodeScanned)
    
    setTimeout(() => {
      _.isNil(window?.printflo_api) && socketOnBarcodeScanned();
    }, 1000);

    return () => {
      if(listner) listner();
      socket.removeAllListeners(["on-barcode-scanned"]);
    }
    // eslint-disable-next-line
  }, []);

  const updateItem = useMutation(
    async ({ condition, update }) => {
      return await submitItem({ condition, update });
    },
    {
      onError: (error) => {
        console.log("[productions:updateItem] error: ", error);
        const retval = errorHandler(error);
        setMsg(retval);
      },
      onSuccess: (data) => {
        // console.log("[productions:updateItem] data: ", data);
        // setModalPreloaderIsOpen(false);
        if (searchTxt) {
          fetchSearchResults.mutate(searchTxt);
        } else {
          refetch();
        }
      },
    }
  );

  const showModalHandler = (mode, item) => {
    console.log("* showModalHandler init");
    // console.log("- mode: ", mode);
    // console.log("- item: ", item);
    setMode(mode);
    if (msg) setMsg(null);
    if (mode === "create") {
      setItem(initialItem);
    } else {
      // get graphics
      const query = { sku: item.sku };
      const options = { pagination: false };
      getGraphics({ query, options })
        .then((result) => {
          // console.log('[showModalHandler:getGraphics] result: ', result)
          item._graphics = result.docs;
          // console.log('[showModalHandler:getGraphics] query: ', query)

          return getInventoryAreas({ query, options });
        })
        .then((result) => {
          // console.log('[showModalHandler:getInventoryAreas] result: ', result)
          item._inventoryArea = result.docs;
          setItem(item);
          componentValueRef.current = item?._component?.sku
            ? item._component.sku
            : item?.component
            ? item.component
            : "";
        });
    }
    
    setTimeout(() => {
      setItemModalIsOpen(true);
    }, 500) 

  };

  const submitHandler = () => {
    // e.preventDefault();
    console.log("* submitHandler init");
    console.log("[submitHandler] item: ", item);
    delete item.stock;
    item["modifiedAt"] = new Date();
    if (mode === "create") {
      item["createdAt"] = new Date();
    }

    if (!_.isEmpty(item.sku)) {
      updateItem.mutate({
        condition: { sku: item.sku },
        update: { ...item, modifiedAt: new Date() },
      });
    }
  };

  const deleteHandler = useMutation(
    async () => {
      return await deleteItem({_id: item._id});
    },
    {
      onSuccess: () => {
        if (searchTxt) {
          fetchSearchResults.mutate(searchTxt);
        } else {
          refetch();
        }
      },
    }
  );

  const handleDelete = () => {
    itemModalIsOpen && setItemModalIsOpen(false);
    deleteHandler.mutate();
  };

  const fetchSearchResults = useMutation(
    async (searchTxt) => {
      console.log("* fetchSearchResults init");
      // console.log("[fetchSearchResults] searchTxt: ", searchTxt);
      setLoading(true);
      return await fetchItemsSearch({
        searchTxt,
        type: selectedItemsType,
        options: { page, limit },
      });
    },
    {
      onError: (error) => {
        setMsg(error);
        setLoading(false);
      },
      onSuccess: (result) => {
        // console.log("- fetchSearchResults onSuccess result: ", result);
        queryClient.setQueryData(["productions", page], () => {
          return { ...result };
        });
        // setLimit(result?.limit)
        setLoading(false);
      },
    }
  );

  const handleSearch = async (searchTxt) => {
    console.log("* handleSearch init");
    // console.log("[handleSearch] searchTxt", searchTxt);
    if (!_.isEmpty(searchTxt)) {
      if (page > 1) {
        alert("Required to move page 1");
        return;
      }
      setSearchTxt(searchTxt);
      fetchSearchResults.mutate(searchTxt);
    }
  };

  const exportItems = async () => {
    console.log("* productions:exportItems init");
    console.log("[productions:exportItems] selectedExportMode: ", selectedExportMode);
    setLoading(true);
    setMsg(null);
    let exportItems;

    if (selectedExportMode === "all") {
      setSelectedExportMode("retrieved");
      console.log("[productions:exportItems:selectedExportMode:all] searchTxt: ", searchTxt);
      if (_.isNil(searchTxt)) {
        exportItems = await fetchItems({
          query: { type: selectedItemsType },
          options: {
            pagination: false,
          },
        });
      } else {
        exportItems = await fetchItemsSearch({
          searchTxt,
          type: selectedItemsType,
          options: { pagination: false },
        });
      }
    } else {
      exportItems = _.cloneDeep(items);
    }
    // console.log("[exportItems] exportItems: ", exportItems);
    if (_.has(exportItems, "docs")) exportItems = exportItems.docs;

    let fields = [
      "sku",
      "description",
      "category",
      "color",
      "size",
      "childSKUs",
      "component",
      "graphicPosition",
      "ripEnv",
      "dimensionalUnit",
      "type",
      "imageUrl",
    ];

    if (settings?.usePretreatmentBarcode) {
      fields.push("pretreatmentBarcode");
    }

    if(settings?.useRecipe) {
      fields.splice(9, 0, 'polarisRecipe')
    }
    
    if(settings?.useHFtemplate) {
      fields.splice(10, 0, 'hfTemplate')
    }
    // console.log("[exportItems] fields: ", fields);

    const result = jsonToCSV({
      fields,
      data: exportItems,
    });
    // console.log(result);

    const element = document.createElement("a");
    const file = new Blob([result], {
      type: "text/csv",
    });
    element.href = URL.createObjectURL(file);
    element.download = `items_${moment().format("YYYYMMDDHHmm")}.csv`;
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();

    setLoading(false);
    setTimeout(exportItems = null, 1000)
  };

  const importItems = (parsedData) => {
    console.log("* importItems init");

    setLoading(true);
    let tempItems = parsedData.map((item) => item.data);
    // tempItems = tempItems.slice(126883, 126983);

    // console.log("- tempItems", tempItems);
    let msg = `Total number of items: ${tempItems.length}`;
    setMsg(msg);

    let i = 0;
    let len = tempItems.length - 1;

    async function loop() {
      const tempItem = tempItems[i];
      // console.log('- tempItem: ', i, tempItem);

      if (!tempItem?.type) {
        tempItem["type"] = selectedItemsType;
      } else {
        if (tempItem?.type !== selectedItemsType) {
          tempItem["type"] = selectedItemsType;
        }
      }
      let sku = _.trimStart(_.trimEnd(tempItem.sku));
      let strChildSKUs = _.has(tempItem, "childSKUs")
        ? tempItem.childSKUs
        : null;

      console.log("- strChildSKUs", strChildSKUs, typeof strChildSKUs);
      console.log("- strChildSKUs includes []", _.includes(strChildSKUs, "[]"));

      if (_.includes(strChildSKUs, "[") || _.includes(strChildSKUs, "]")) {
        strChildSKUs = strChildSKUs.replace("[", "").replace("]", "").trim();
        console.log("- strChildSKUs after replace []", strChildSKUs);
      }

      if (!_.isNil(strChildSKUs) && !_.isEmpty(strChildSKUs)) {
        if (_.includes(strChildSKUs, ",")) {
          let arrChildSKUs = strChildSKUs.split(",").map((child) => {
            const regEx = new RegExp(/^ /)
            // console.log("regEx.test(child): ", regEx.test(child))
            if(regEx.test(child)) {
              return _.trimStart(child)
            } else {
              return child
            }
          })
          tempItem.childSKUs = arrChildSKUs;

        }
      } else {
        tempItem.childSKUs = [];
      }

      // find component is exists

      const componentSKU = tempItem?.component ? tempItem?.component : null;
      if (componentSKU) {
        try {
          const componentDoc = await getItemDoc(componentSKU);
          // console.log('- componentDoc: ', componentDoc)
          if (!componentDoc) {
            await submitItem({
              condition: { sku: componentSKU },
              update: {
                sku: componentSKU,
                type: "component",
                createdAt: new Date(),
              },
            });
          }
        } catch (error) {
          console.error("- getItemDoc error: ", error);
        }
      }

      try {
        if(!_.isNil(sku) && !_.isEmpty(sku)) {
          const result = await submitItem({
            condition: { sku },
            update: { ...tempItem, modifiedAt: new Date() },
          });
          setMsg(`${i + 1}/${tempItems.length}: ${result.sku} updated.`);
        } else {
          setMsg(`${i + 1}/${tempItems.length}: sku is null or empty.`);
        }
        i += 1;
      } catch (error) {
        console.log("submit item error", error);
        setMsg(`Error occured, try again from this sku: ${sku}`);
        return;
      }

      if (i <= len && _.has(tempItem, "sku")) {
        loop();
      } else {
        setMsg("* Completed import items");
        setLoading(false);
        return;
      }
    }

    if (i <= len) {
      loop();
    }
  };

  const handleOnPageChange = ({ selected }) => {
    // console.log("handleOnPageChange page", selected);
    setPage(selected + 1);
  };

  const handleGeneratePMT = async (e, item) => {
    console.log("* handleGeneratePMT init");
    console.log("- item: ", item);
    e.preventDefault();
    setLoading(true);
    try {
      const result = await generatePMTHandler(item, settings);
      console.log("- result: ", result);
      const { pretreatmentBarcode, message } = result;
      if (pretreatmentBarcode) {
        setItem({ ...item, pretreatmentBarcode: result.pretreatmentBarcode });
        updateItem.mutate({
          condition: { sku: item.sku },
          update: { pretreatmentBarcode, modifiedAt: new Date() },
        });
      }
      if (message) {
        toast.error(message, {
          position: "bottom-right",
        });
      }
      setLoading(false);
    } catch (error) {
      console.log("- generatePMTHandler error: ", error);
      setLoading(false);
      toast.error(error, {
        position: "bottom-right",
      });
    }
  };

  const printSKULabel = async (item) => {
    console.log("* printSKULabel init");
    // console.log("- item: ", item)
    let printQty = 1
    if (window?.printflo_api) {
      printQty = await window.printflo_api._prompt({message: "Please enter a number to print", defaultValue: 1});
    } else {
      printQty = prompt("Please enter a number to print", 1);
    }
    // console.log("[printSKULabel] printQty: ", printQty)
    let zpl = skuLabelTemplateQR({ settings, item, printQty });
    // console.log('[printSKULabel] zpl: ', zpl)
    if(window?.printflo_api) {
      zpl && window.printflo_api._zplPrint({template: zpl});
    } else {
      zpl && socket.emit("on-sku-print", {template: zpl}, cb => {
        console.log("[printSKULabel:on-sku-print] cb: ", cb)
        if (cb?.name === "Error") {
          toast.error(cb?.message, {
            position: "bottom-right",
          });
        } else {
          toast.info(cb?.message, {
            position: "bottom-right",
            autoClose: 1500,
          });
        }
      })

    }
  };

  const batchDelete = (parsedData) => {
    console.log("* batchDelete init");
    try {
      setLoading(true);
      setMsg(null);
      let temp = parsedData.map((item) => item.data);
      let msg = `Total number of productions for deleting: ${temp.length}`;
      setMsg(msg);
      let i = 0;
      let len = temp.length - 1;

      async function loop() {
        let sku = temp[i].sku;
        try {
          await deleteItem({ sku });
          // console.log("deleteItemBySku result", result);
          setMsg(`${i + 1}/${temp.length}: ${sku} deleted.`);
          i += 1;
        } catch (error) {
          console.log("deleteItemBySku error", error);
          const retval = errorHandler(error);
          console.log("deleteItemBySku retval", retval);
          setMsg(retval);
          setLoading(false);
          return;
        }

        if (i <= len && _.has(temp[i], "sku")) {
          loop();
        } else {
          setMsg(
            "* Completed batch deletion of the finished goods, click refresh to load a page."
          );
          setLoading(false);
          return;
        }
      }

      if (i <= len) {
        loop();
      }
    } catch (error) {
      const retval = errorHandler(error);
      console.log("- batchDelete error: ", retval);
      setMsg(retval);
    }
  };

  const onAfterCloseModal = () => {
    console.log("* onAfterCloseModal init");
    if (_.isNil(searchTxt)) {
      refetch();
    } else {
      fetchSearchResults.mutate(searchTxt);
    }
  };

  return (
    <>
      <Title title={title} />
      <ToastContainer theme="dark" />
      <PageMainHeader title={title} user={user} settings={settings} />

      <section className="primary productions">
        {isLoading ? (
          <Skeleton count={20} height={50} circle={true} />
        ) : isError ? (
          <Banner variant="danger" className="mb-10">
            {error?.message ? error.message : error}
          </Banner>
        ) : (
          <>
            <PageMenuHeader>
              <ItemsHeader
                showModal={showModalHandler}
                importHandler={importItems}
                exportHandler={exportItems}
                handleSearch={handleSearch}
                selectedExportMode={selectedExportMode}
                setSelectedExportMode={setSelectedExportMode}
                batchDeleteHandler={batchDelete}
                handleDelete={handleDelete}
              />
              <div className="align-right_container">
                <Search handleSearch={handleSearch} />
              </div>
            </PageMenuHeader>

            {items && (
              <Pagination
                handleOnPageChange={handleOnPageChange}
                totalDocs={items?.totalDocs}
                totalPages={items?.totalPages}
                page={page}
                limit={limit}
              />
            )}

            {msg && <Banner className="mb-10">{msg}</Banner>}

            {items && Boolean(items?.docs.length) && (
              <ProductionsList
                items={items.docs}
                showModal={showModalHandler}
                printSKULabel={printSKULabel}
                settings={settings}
              />
            )}
            {searchTxt && !Boolean(items?.docs.length) && !loading && (
              <Banner>
                <span>Items not found.</span>
              </Banner>
            )}
          </>
        )}
        <ScrollTop />
      </section>
      <ModalItem
        modalIsOpen={itemModalIsOpen}
        setModalIsOpen={setItemModalIsOpen}
        title={"Production"}
        mode={mode}
        item={item}
        setItem={setItem}
        submitHandler={submitHandler}
        selectedItemsType={selectedItemsType}
        settings={settings}
        handleGeneratePMT={handleGeneratePMT}
        user={user}
        handleDelete={handleDelete}
        onAfterCloseModal={onAfterCloseModal}
        componentValueRef={componentValueRef}
      />

      <ModalPreloader modalPreloaderIsOpen={isFetching || loading} />
    </>
  );
}
